import React, { useEffect, useState } from "react";
import LoadingOverlay from "./loadingOverlay";
import "react-datepicker/dist/react-datepicker.css";
import { Box, Paper, Alert, Typography, Divider, Chip } from "@mui/material";
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    LineChart, Line, ScatterChart, Scatter, ReferenceArea,
    Legend, ComposedChart, Area, Bar, BarChart
} from 'recharts';
import moment from "moment";

import { average_daily_load_curve, esg_smart_meter_max_demand, average_weekday_weekend_curve, esg_load_gj } from "../api/smartmeter";
import { battery_average_daily_load_curve, battery_max_demand, battery_average_weekday_weekend } from "../api/meter";
import { solarAnalysisData, gridAnalysisData, loadAnalysisData, SMsolarAnalysisData, SMgridAnalysisData, SMloadAnalysisData } from "../api/bill";
import { fetchSiteDetails } from "../api/plant";
import colors from "../colour";


const DAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_COLORS = {
    Monday: "#5e35b1",
    Tuesday: "#1e88e5",
    Wednesday: "#43a047",
    Thursday: "#fb8c00",
    Friday: "#e53935",
    Saturday: "#00acc1",
    Sunday: "#8e24aa",
};

const CHART_MARGIN = { top: 10, right: 20, left: 60, bottom: 60 };

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "#5e35b1", letterSpacing: 0.2 }}>
        {children}
    </Typography>
);

const ChartCard = ({ title, children, chip }) => (
    <Paper elevation={0} sx={{
        p: 3,
        borderRadius: 4,
        border: "1px solid #ede7f6",
        background: "#fff",
    }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <SectionTitle>{title}</SectionTitle>
            {chip && (
                <Chip
                    label={chip}
                    size="small"
                    sx={{ backgroundColor: "#ede7f6", color: "#5e35b1", fontWeight: 600, fontSize: 12 }}
                />
            )}
        </Box>
        <Divider sx={{ mb: 2, borderColor: "#f3e5f5" }} />
        {children}
    </Paper>
);

const commonXAxisProps = (data) => ({
    dataKey: "time",
    tickFormatter: (tick) => tick,
    ticks: data.map(d => d.time).filter(t => t && t.endsWith(":00") && t !== "00:00"),
    tick: { fontSize: 11, fill: "#888" },
    label: { value: "", position: "insideBottom", offset: -45, style: { fontWeight: 600, fill: "#555" } },
});

const commonYAxisProps = {
    tickFormatter: (val) => Number(val).toFixed(0),
    tick: { fontSize: 11, fill: "#888" },
    label: { value: "Power (kW)", angle: -90, position: "insideLeft", offset: -45, style: { fontWeight: 600, fill: "#555" } },
};

const mergeGraphData = (solarData, gridData, loadData) => {
    const dataMap = {};
    solarData.forEach(({ period, daily_solar_generation_kwh }) => {
        if (!dataMap[period]) dataMap[period] = { period };
        dataMap[period].daily_solar_generation_kwh = daily_solar_generation_kwh;
    });
    gridData.forEach(({ period, grid_consumption_kwh, grid_export_kwh }) => {
        if (!dataMap[period]) dataMap[period] = { period };
        dataMap[period].daily_grid_consumption_kwh = grid_consumption_kwh;
        dataMap[period].grid_export_kwh = grid_export_kwh;
    });
    loadData.forEach(({ period, load_consumption_kwh }) => {
        if (!dataMap[period]) dataMap[period] = { period };
        dataMap[period].daily_load_consumption_kwh = load_consumption_kwh;
    });
    return Object.values(dataMap);
};

const SMmergeGraphData = (solarData, gridData, loadData) => {
    const dataMap = {};
    solarData.forEach(({ recorded_at, total_active_power_kwh }) => {
        if (!dataMap[recorded_at]) dataMap[recorded_at] = { recorded_at };
        dataMap[recorded_at].daily_solar_generation_kwh = total_active_power_kwh;
    });
    gridData.forEach(({ recorded_at, total_active_power_kwh, grid_export_kwh }) => {
        if (!dataMap[recorded_at]) dataMap[recorded_at] = { recorded_at };
        dataMap[recorded_at].daily_grid_consumption_kwh = total_active_power_kwh;
        dataMap[recorded_at].grid_export_kwh = grid_export_kwh;
    });
    loadData.forEach(({ recorded_at, total_active_power_kwh }) => {
        if (!dataMap[recorded_at]) dataMap[recorded_at] = { recorded_at };
        dataMap[recorded_at].daily_load_consumption_kwh = total_active_power_kwh;
    });
    return Object.values(dataMap);
};

const MDReportSection = ({ selectedDate, siteId, solarViewType, userType }) => {

    const [dailyLoadCurve, setDailyLoadCurve] = useState([]);
    const [weekdayWeekendCurve, setWeekdayWeekendCurve] = useState([]);
    const [maxDemand, setMaxDemand] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [siteDetails, setSiteDetails] = useState(null);
    const [solarAnalysis, setSolarAnalysis] = useState([]);
    const [gridAnalysis, setGridAnalysis] = useState([]);
    const [loadAnalysis, setLoadAnalysis] = useState([]);
    const [combinedGraphData, setCombinedGraphData] = useState(null);
    const [loadGJ, setLoadGJ] = useState(null);

    // Fetch energy analysis (solar/grid/load bar chart)
    useEffect(() => {
        if (userType === null) return;
        setIsLoading(true);

        const fetchData = async () => {
            setError(null);
            const formattedDate = moment(selectedDate).format("YYYY-MM");
            const results = await Promise.allSettled([
                solarAnalysisData(siteId, formattedDate),
                gridAnalysisData(siteId, formattedDate),
                loadAnalysisData(siteId, formattedDate),
            ]);

            const [solarRes, gridRes, loadRes] = results;
            let solarData = null, gridData = null, loadData = null;

            if (solarRes.status === "fulfilled") { solarData = solarRes.value.data; setSolarAnalysis(solarData); }
            if (gridRes.status === "fulfilled") { gridData = gridRes.value.data; setGridAnalysis(gridData); }
            if (loadRes.status === "fulfilled") { loadData = loadRes.value.data; setLoadAnalysis(loadData); }

            // ✅ Plot whatever is available, don't require all three
            if (solarData || gridData || loadData) {
                setCombinedGraphData(mergeGraphData(
                    solarData?.graph_data ?? [],
                    gridData?.grid_graph_data ?? [],
                    loadData?.load_graph_data ?? []
                ));
            } else {
                setCombinedGraphData([]);
                setError("No data available for this period");
            }

            setIsLoading(false);
        };

        const SMfetchData = async () => {
            setError(null);
            try {
                const formattedDate = solarViewType === "day"
                    ? moment(selectedDate).format("YYYY-MM")
                    : moment(selectedDate).format("YYYY");

                const [solarRes, gridRes, loadRes] = await Promise.allSettled([
                    SMsolarAnalysisData(siteId, formattedDate),
                    SMgridAnalysisData(siteId, formattedDate),
                    SMloadAnalysisData(siteId, formattedDate),
                ]);

                const solarData = solarRes.status === 'fulfilled' ? solarRes.value.data : null;
                const gridData = gridRes.status === 'fulfilled' ? gridRes.value.data : null;
                const loadData = loadRes.status === 'fulfilled' ? loadRes.value.data : null;

                if (solarData) setSolarAnalysis(solarData);
                if (gridData) setGridAnalysis(gridData);
                if (loadData) setLoadAnalysis(loadData);

                if (solarData || gridData || loadData) {
                    setCombinedGraphData(SMmergeGraphData(
                        solarData?.graph_data ?? [],
                        gridData?.grid_graph_data ?? [],
                        loadData?.load_graph_data ?? []
                    ));
                    setError(null); // ✅ clear error if at least one succeeded
                } else {
                    setCombinedGraphData([]);
                    setError("No data available for this period"); // ✅ only error if ALL fail
                }

            } catch (err) {
                console.error("Error fetching SM data", err);
                // ✅ Don't set error here — allSettled won't throw unless something unexpected happens
            } finally {
                setIsLoading(false);
            }
        };

        if (userType === 1) SMfetchData();
        else fetchData();

    }, [selectedDate, siteId, solarViewType, userType]);

    // Fetch smart meter specific data
    useEffect(() => {
        if (!siteId || !selectedDate || userType === null) return;

        const SMfetchData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const formattedDate = solarViewType === "day"
                    ? moment(selectedDate).format("YYYY-MM")
                    : moment(selectedDate).format("YYYY");

                const [dailyloadcurve, weekdayweekendcurve, maxdemand, userdetails, loadgj] = await Promise.all([
                    average_daily_load_curve(siteId, formattedDate),
                    average_weekday_weekend_curve(siteId, formattedDate),
                    esg_smart_meter_max_demand(siteId, formattedDate),
                    fetchSiteDetails(siteId),
                    esg_load_gj(siteId, formattedDate)
                ]);

                setDailyLoadCurve(dailyloadcurve.data);
                setWeekdayWeekendCurve(weekdayweekendcurve.data);
                setMaxDemand(maxdemand.data);
                setSiteDetails(userdetails);
                setLoadGJ(loadgj.data);
            } catch (err) {
                console.error("Error fetching data", err);
                setError("Failed to fetch data");
            } finally {
                setIsLoading(false);
            }
        };

        const fetchData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const formattedDate = solarViewType === "day"
                    ? moment(selectedDate).format("YYYY-MM")
                    : moment(selectedDate).format("YYYY");

                const [dailyloadcurve, weekdayweekendcurve, maxdemand, userdetails] = await Promise.all([
                    battery_average_daily_load_curve(siteId, formattedDate),
                    battery_average_weekday_weekend(siteId, formattedDate),
                    battery_max_demand(siteId, formattedDate),
                    fetchSiteDetails(siteId),
                ]);

                setDailyLoadCurve(dailyloadcurve.data);
                setWeekdayWeekendCurve(weekdayweekendcurve.data);
                setMaxDemand(maxdemand.data);
                setSiteDetails(userdetails);
            } catch (err) {
                console.error("Error fetching data", err);
                setError("Failed to fetch data");
            } finally {
                setIsLoading(false);
            }
        };

        if (userType === 1) SMfetchData();
        else fetchData();

    }, [selectedDate, siteId, solarViewType, userType]);
    // Derived data
    const formattedDailyMax = maxDemand?.daily_maxes
        ? (maxDemand.daily_maxes || []).map(d => {
            const timePart = d.timestamp.split(" ")[1];
            const [h, m] = timePart.split(":").map(Number);
            return {
                time: h + m / 60,
                power: parseFloat(d.max_value.toFixed(2)),
                date: d.period,
                label: timePart.slice(0, 5),
            };
        })
        : [];

    const formattedMonthlyMax = maxDemand?.monthly_maxes
        ? (maxDemand.monthly_maxes || []).map(d => ({
            period: d.period,
            timestamp: d.timestamp,
            max_value: d.max_value,
            time: (() => {
                const timePart = d.timestamp.split(" ")[1];
                const [h, m] = timePart.split(":").map(Number);
                return h + m / 60;
            })(),
            label: d.timestamp.split(" ")[1]?.slice(0, 5) || "",
        }))
        : [];

    const monthlyPeak = maxDemand?.monthly_max || maxDemand?.yearly_max || null;

    // Determine which data to show based on solarViewType
    const showDailyScatter = solarViewType === "day" && formattedDailyMax.length > 0;
    const showMonthlyScatter = solarViewType !== "day" && formattedMonthlyMax.length > 0;

    // Use appropriate data for the chart
    const scatterData = showDailyScatter ? formattedDailyMax : formattedMonthlyMax;
    const xAxisDomain = showDailyScatter ? [0, 24] : [0, 24]; // Keep 0-24 for time of day
    const yAxisMax = scatterData.length > 0
        ? Math.ceil((Math.max(...scatterData.map(d => d.power || d.max_value)) + 50) / 50) * 50
        : 300;

    const tariff = maxDemand?.tariff || siteDetails?.tariff || "";
    const showPeakHighlight = tariff.toUpperCase().includes("T");

    const formattedWeekdayCurve = weekdayWeekendCurve.map(d => ({
        time: d.time,
        weekday_avg: d.weekday_mean,
        weekday_range: [Math.max(0, d.weekday_lower), d.weekday_upper],
        weekend_avg: d.weekend_mean,
        weekend_range: [Math.max(0, d.weekend_lower), d.weekend_upper],
        weekday_lower_raw: d.weekday_lower,
        weekday_upper_raw: d.weekday_upper,
        weekend_lower_raw: d.weekend_lower,
        weekend_upper_raw: d.weekend_upper,
    }));

    const energyBarXKey = userType === 1 ? "recorded_at" : "period";
    const mdBarXKey = userType === 1 ? "recorded_at" : "period";
    const mdBarData = userType === 1
        ? gridAnalysis?.grid_graph_data
        : gridAnalysis?.grid_graph_data;

    if (isLoading) return <LoadingOverlay />;
    // For the Maximum Demand bar chart (raw shape, not the scatter-transformed one)
    const barChartData = solarViewType === "day"
        ? (maxDemand?.daily_maxes ?? [])
        : (maxDemand?.monthly_maxes ?? []);

    return (
        <>
            <svg width="0" height="0" style={{ position: 'absolute' }}>
                <defs>
                    <pattern id="diagonalHatch" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                        <line x1="0" y1="0" x2="0" y2="8" stroke="#ffffff" strokeWidth="3" />
                    </pattern>
                </defs>
            </svg>
            <Box sx={{ backgroundColor: colors.l7, borderRadius: "20px", p: 3 }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 3 }}>{error}</Alert>}

                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

                    {/* ── Monthly Peak Summary ── */}
                    {gridAnalysis?.grid_graph_data?.length > 0 && monthlyPeak && (
                        <Paper elevation={0} sx={{
                            p: 3, borderRadius: 4,
                            border: "1px solid #ede7f6",
                            background: "linear-gradient(135deg, #421585 0%, #ddd3ec 100%)",
                        }}>
                            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: "#fff" }}>
                                Monthly Peak Demand
                            </Typography>
                            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                                {[
                                    { label: "Peak Value", value: `${monthlyPeak.max_value.toFixed(1)} kW`, highlight: true },
                                    { label: "Date", value: monthlyPeak.date },
                                    { label: "Time", value: monthlyPeak.timestamp.split(" ")[1].slice(0, 5) },
                                    { label: "Tariff", value: tariff || "-" },
                                    { label: "Metric", value: maxDemand?.metric_used || "-" },
                                ].map(({ label, value, highlight }) => (
                                    <Box key={label} sx={{
                                        background: "rgba(255, 255, 255, 0.15)",
                                        backdropFilter: "blur(10px)",
                                        WebkitBackdropFilter: "blur(10px)",
                                        borderRadius: 3,
                                        px: 2.5, py: 1.5,
                                        minWidth: 110,
                                        border: highlight
                                            ? "1.5px solid rgba(255, 255, 255, 0.6)"
                                            : "1px solid rgba(255, 255, 255, 0.25)",
                                        boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                                    }}>
                                        <Typography variant="caption" sx={{ display: "block", mb: 0.3, color: "rgba(255,255,255,0.75)" }}>
                                            {label}
                                        </Typography>
                                        <Typography
                                            variant={highlight ? "h5" : "body1"}
                                            fontWeight={highlight ? 700 : 500}
                                            sx={{ color: "#fff" }}
                                        >
                                            {value}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Paper>
                    )}
                    {/* ── Daily Max Demand Scatter ── */}
                    {gridAnalysis?.grid_graph_data?.length > 0 && monthlyPeak && (
                        <ChartCard
                            title={showDailyScatter ? "Daily Maximum Demand Occurrence" : "Monthly Maximum Demand Occurrence"}
                            chip={tariff || undefined}
                        >
                            <ResponsiveContainer width="100%" height={400}>
                                <ScatterChart margin={{ top: 20, right: 40, bottom: 40, left: 20 }}>
                                    <defs>
                                        <linearGradient id="scatterGlow" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="#7e4a9d" stopOpacity={0.9} />
                                            <stop offset="100%" stopColor="#b39ddb" stopOpacity={0.2} />
                                        </linearGradient>
                                    </defs>

                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0e6ff" />

                                    {showPeakHighlight && showDailyScatter && (
                                        <ReferenceArea
                                            x1={14} x2={22}
                                            fill="#fff3e0"
                                            fillOpacity={0.6}
                                            stroke="#ff9800"
                                            strokeOpacity={0.4}
                                            label={{ value: "Peak Period (14:00–22:00)", position: "insideTop", fontSize: 11, fill: "#e65100", fontWeight: "bold" }}
                                        />
                                    )}

                                    <XAxis
                                        dataKey="time"
                                        type="number"
                                        domain={xAxisDomain}
                                        ticks={[0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24]}
                                        tickFormatter={(v) => `${String(v).padStart(2, "0")}:00`}
                                        tick={{ fontSize: 11, fill: "#888" }}
                                        label={{ value: "Time of Occurrence", position: "bottom", offset: 20, style: { fill: "#555", fontWeight: 600 } }}
                                    />
                                    <YAxis
                                        dataKey={showDailyScatter ? "power" : "max_value"}
                                        type="number"
                                        domain={[0, yAxisMax]}
                                        tick={{ fontSize: 11, fill: "#888" }}
                                        label={{ value: "Power (kW)", angle: -90, position: "insideLeft", offset: -5, style: { fill: "#555", fontWeight: 600 } }}
                                    />

                                    <Tooltip
                                        cursor={{ strokeDasharray: "3 3" }}
                                        content={({ payload }) => {
                                            if (!payload?.length) return null;
                                            const point = payload[0].payload;
                                            if (showDailyScatter) {
                                                const { date, label, power } = point;
                                                return (
                                                    <Box sx={{ background: "#fff", border: "1px solid #ede7f6", borderRadius: 2, p: 1.5, boxShadow: "0 4px 16px rgba(94,53,177,0.1)" }}>
                                                        <Typography variant="body2" fontWeight={700} color="#5e35b1">{date}</Typography>
                                                        <Typography variant="body2" color="text.secondary">Time: <b>{label}</b></Typography>
                                                        <Typography variant="body2" color="text.secondary">Max Demand: <b>{power} kW</b></Typography>
                                                    </Box>
                                                );
                                            } else {
                                                const { period, label, max_value } = point;
                                                return (
                                                    <Box sx={{ background: "#fff", border: "1px solid #ede7f6", borderRadius: 2, p: 1.5, boxShadow: "0 4px 16px rgba(94,53,177,0.1)" }}>
                                                        <Typography variant="body2" fontWeight={700} color="#5e35b1">{period}</Typography>
                                                        <Typography variant="body2" color="text.secondary">Time: <b>{label}</b></Typography>
                                                        <Typography variant="body2" color="text.secondary">Max Demand: <b>{max_value} kW</b></Typography>
                                                    </Box>
                                                );
                                            }
                                        }}
                                    />

                                    <Scatter
                                        data={scatterData}
                                        fill="url(#scatterGlow)"
                                        stroke="#7e4a9d"
                                        strokeWidth={1}
                                    />
                                </ScatterChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    )}
                    {/* ── Average Daily Load Curve ── */}
                    {dailyLoadCurve.length > 0 && (
                        <ChartCard title="Average Daily Load Curve">
                            <ResponsiveContainer width="100%" height={400}>
                                <LineChart data={dailyLoadCurve} margin={CHART_MARGIN}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0e6ff" />
                                    <XAxis {...commonXAxisProps(dailyLoadCurve)} />
                                    <YAxis {...commonYAxisProps} />
                                    <Tooltip
                                        contentStyle={{ backgroundColor: "#fff", border: "1px solid #ede7f6", borderRadius: 8 }}
                                        formatter={(value, name) => [`${Number(value).toFixed(2)} kW`, name]}
                                    />
                                    <Legend
                                        verticalAlign="bottom"
                                        align="center"
                                        wrapperStyle={{ paddingTop: 20 }}
                                        formatter={(value) => {
                                            const color = DAY_COLORS[value] || "#555";
                                            return <span style={{ fontSize: 15, color: color }}>{value}</span>;
                                        }}
                                        iconType="square"
                                    />
                                    {DAY_ORDER.map((day) => (
                                        <Line key={day} name={day} type="monotone" dataKey={day}
                                            stroke={DAY_COLORS[day]} strokeWidth={2} dot={false} connectNulls />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    )}
                    {/* ── Weekday vs Weekend Curve ── */}
                    {weekdayWeekendCurve.length > 0 && (
                        <ChartCard title="Weekday vs Weekend Load Curve">
                            <ResponsiveContainer width="100%" height={400}>
                                <ComposedChart data={formattedWeekdayCurve} margin={CHART_MARGIN}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0e6ff" />
                                    <XAxis {...commonXAxisProps(formattedWeekdayCurve)} />
                                    <YAxis {...commonYAxisProps} />

                                    <Tooltip
                                        content={({ payload, label }) => {
                                            if (!payload?.length) return null;
                                            const d = payload[0]?.payload;
                                            return (
                                                <Box sx={{ background: "#fff", border: "1px solid #ede7f6", borderRadius: 2, p: 1.5, boxShadow: "0 4px 16px rgba(94,53,177,0.1)" }}>
                                                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>{label}</Typography>
                                                    <Typography variant="body2" sx={{ color: "#7e4a9d" }}>Weekday Avg: <b>{Number(d.weekday_avg).toFixed(2)} kW</b></Typography>
                                                    <Typography variant="body2" sx={{ color: "#b39ddb" }}>Weekday Range: {Number(d.weekday_lower_raw).toFixed(2)} – {Number(d.weekday_upper_raw).toFixed(2)} kW</Typography>
                                                    <Typography variant="body2" sx={{ color: "#1565c0" }}>Weekend Avg: <b>{Number(d.weekend_avg).toFixed(2)} kW</b></Typography>
                                                    <Typography variant="body2" sx={{ color: "#42a5f5" }}>Weekend Range: {Number(d.weekend_lower_raw).toFixed(2)} – {Number(d.weekend_upper_raw).toFixed(2)} kW</Typography>
                                                </Box>
                                            );
                                        }}
                                    />

                                    <Legend
                                        verticalAlign="bottom"
                                        align="center"
                                        wrapperStyle={{ paddingTop: 20 }}
                                        formatter={(value, entry) => {
                                            let color = "#555";
                                            if (value === "Weekday Range") color = "#b39ddb";
                                            if (value === "Weekend Range") color = "#90caf9";
                                            if (value === "Weekday Avg") color = "#7e4a9d";
                                            if (value === "Weekend Avg") color = "#1565c0";
                                            return <span style={{ fontSize: 15, color: color }}>{value}</span>;
                                        }}
                                        iconType="square"
                                    />

                                    <Area name="Weekday Range" dataKey="weekday_range" fill="#b39ddb" fillOpacity={0.35} stroke="none" />
                                    <Area name="Weekend Range" dataKey="weekend_range" fill="#90caf9" fillOpacity={0.35} stroke="none" />
                                    <Line name="Weekday Avg" type="monotone" dataKey="weekday_avg" stroke="#7e4a9d" strokeWidth={3} dot={false} connectNulls />
                                    <Line name="Weekend Avg" type="monotone" dataKey="weekend_avg" stroke="#1565c0" strokeWidth={3} dot={false} connectNulls />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    )}
                    {/* ── Energy Analysis Chart ── */}
                    <ChartCard title="Energy Analysis">
                        <ResponsiveContainer width="100%" height={400}>
                            <ComposedChart
                                data={combinedGraphData}
                                margin={{ top: 0, right: 0, bottom: 25, left: 28 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0e6ff" />
                                <XAxis dataKey={energyBarXKey} tick={{ fontSize: 12, fill: colors.f3 }} />
                                <YAxis
                                    tick={{ fontSize: 12, fill: colors.f3 }}
                                    label={{ fill: colors.f3, value: "Energy (kWh)", angle: -90, fontSize: 13, dx: -50 }}
                                />
                                <Tooltip
                                    cursor={{ fill: "rgba(106,90,205,0.1)" }}
                                    content={({ payload, label }) => {
                                        if (!payload?.length) return null;

                                        // Find matching GJ data for this date
                                        const gjEntry = loadGJ?.load_graph_data_gj?.find(
                                            (d) => d.recorded_at === label
                                        );

                                        // Map each kWh dataKey to its GJ counterpart
                                        const gjFieldMap = {
                                            daily_solar_generation_kwh: "solar_active_power_gj",
                                            daily_grid_consumption_kwh: "grid_import_power_gj",
                                            grid_export_kwh: "grid_export_power_gj",
                                            daily_load_consumption_kwh: "total_load_power_gj",
                                        };

                                        return (
                                            <Box sx={{ background: "#fff", border: "1px solid #ede7f6", borderRadius: 2, p: 1.5, boxShadow: "0 4px 16px rgba(94,53,177,0.1)" }}>
                                                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>Date: {label}</Typography>
                                                {payload.map((entry, i) => {
                                                    const gjField = gjFieldMap[entry.dataKey];
                                                    const gjValue = gjEntry && gjField ? gjEntry[gjField] : null;
                                                    return (
                                                        <Typography key={i} variant="body2" sx={{ color: entry.color }}>
                                                            {entry.name}: {entry.value ? entry.value.toFixed(2) : 0} kWh
                                                            {gjValue !== null && gjValue !== undefined ? ` / ${gjValue.toFixed(2)} GJ` : ""}
                                                        </Typography>
                                                    );
                                                })}
                                            </Box>
                                        );
                                    }}
                                />
                                <Legend
                                    onClick={(e) => {
                                        const { dataKey } = e;
                                        setCombinedGraphData(prev => prev.map(entry =>
                                            entry[dataKey] === null
                                                ? { ...entry, [dataKey]: entry[`_${dataKey}`] }
                                                : { ...entry, [`_${dataKey}`]: entry[dataKey], [dataKey]: null }
                                        ));
                                    }}
                                />
                                <Bar
                                    dataKey="daily_solar_generation_kwh"
                                    name="Solar Generation"
                                    fill={colors.g7}
                                    barSize={20}
                                    radius={[3, 3, 0, 0]}
                                />

                                <Bar
                                    dataKey="daily_grid_consumption_kwh"
                                    name="Grid Import"
                                    fill={colors.g1}
                                    barSize={20}
                                    radius={[3, 3, 0, 0]}
                                />

                                <Bar
                                    dataKey="grid_export_kwh"
                                    name="Grid Export"
                                    fill={colors.g1}
                                    fillOpacity={0.5}
                                    barSize={20}
                                    radius={[0, 0, 3, 3]}
                                />

                                <Line
                                    type="monotone"
                                    dataKey="daily_load_consumption_kwh"
                                    name="Load"
                                    stroke={colors.g11}
                                    strokeWidth={2}
                                    strokeDasharray="5 3"
                                    dot={{ r: 4, fill: colors.g11, strokeWidth: 0 }}
                                    connectNulls
                                />
                            </ComposedChart>
                        </ResponsiveContainer>
                    </ChartCard>

                    {/* ── Maximum Demand Analysis Bar Chart ── */}
                    {barChartData?.length > 0 && (
                        <ChartCard title="Maximum Demand Analysis">
                            <ResponsiveContainer width="100%" height={400}>
                                <BarChart
                                    data={barChartData}
                                    margin={{ top: 0, right: 0, bottom: 25, left: 28 }}
                                >
                                    <defs>
                                        <pattern id="diagonalHatch" patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
                                            <line x1={0} y1={0} x2={0} y2={6} stroke="#fff" strokeWidth={2} />
                                        </pattern>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={colors.f1} />
                                    <XAxis dataKey="period" tick={{ fontSize: 12, fill: colors.f3 }} />
                                    <YAxis
                                        tick={{ fontSize: 12, fill: colors.f3 }}
                                        label={{ fill: colors.f3, value: "Max Power (kW)", angle: -90, fontSize: 13, dx: -50 }}
                                    />
                                    <Tooltip
                                        cursor={{ fill: "rgba(106,90,205,0.1)" }}
                                        content={({ payload, label }) => {
                                            if (!payload?.length) return null;
                                            const { timestamp, max_value, load_kw, solar_kw } = payload[0].payload;
                                            const timeOnly = timestamp ? timestamp.split(" ")[1] : "-";
                                            return (
                                                <Box sx={{ background: "#fff", border: "1px solid #ede7f6", borderRadius: 2, p: 1.5, boxShadow: "0 4px 16px rgba(94,53,177,0.1)" }}>
                                                    <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>Date: {label}</Typography>
                                                    <Typography variant="body2" color="text.secondary">Time: <b>{timeOnly}</b></Typography>
                                                    <Typography variant="body2" sx={{ color: colors.g1 }}>Max Value: <b>{max_value?.toFixed(2)} kW</b></Typography>
                                                    <Typography variant="body2" color={colors.g3}>Load: <b>{load_kw?.toFixed(2)} kW</b></Typography>
                                                    <Typography variant="body2" color={colors.g2}>Solar: <b>{solar_kw?.toFixed(2)} kW</b></Typography>
                                                </Box>
                                            );
                                        }}
                                    />
                                    <Legend
                                        content={({ payload }) =>
                                            payload?.length ? (
                                                <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 8 }}>
                                                    {payload.map((entry, i) => (
                                                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                                            <svg width={20} height={14}>
                                                                <rect x={0} y={0} width={20} height={14} fill={entry.color} rx={2} ry={2} />
                                                                <rect x={0} y={0} width={20} height={14} fill="url(#diagonalHatch)" rx={2} ry={2} />
                                                            </svg>
                                                            <span style={{ fontSize: 13, color: entry.color }}>{entry.value}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : null
                                        }
                                    />
                                    <Bar
                                        dataKey="max_value"
                                        name="Max Power of Grid"
                                        fill={colors.g1}
                                        barSize={20}
                                        radius={[3, 3, 0, 0]}
                                        shape={(props) => {
                                            const { x, y, width, height } = props;
                                            if (height <= 0) return null;
                                            return (
                                                <g>
                                                    <rect x={x} y={y} width={width} height={height} fill={colors.g1} rx={3} ry={3} />
                                                    <rect x={x} y={y} width={width} height={height} fill="url(#diagonalHatch)" rx={3} ry={3} />
                                                </g>
                                            );
                                        }}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </ChartCard>
                    )}


                </Box>
            </Box>
        </>
    );
};

export default MDReportSection;